// Share an artifact by link (Adam, 13 Sep 2026). Opening the dialog mints
// the link (or fetches the one that exists); "Stop sharing" takes it back.
// Sharing is deliberate: nothing is reachable by URL until this runs.
import React, { useEffect, useRef, useState } from 'react'
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControlLabel, IconButton, InputAdornment, Switch, TextField, Tooltip, Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import LinkIcon from '@mui/icons-material/Link'
import useArtifactShare from '../../hooks/useArtifactShare'
import useAccount from '../../hooks/useAccount'

export interface ShareArtifactDialogProps {
  open: boolean
  onClose: () => void
  artifactId: string | null
  /** What to call the file in the dialog. */
  label: string
}

export default function ShareArtifactDialog({ open, onClose, artifactId, label }: ShareArtifactDialogProps) {
  const { state, handleShare, handleRevoke, handleSetDiscuss, reset } = useArtifactShare()
  // The discuss switch is Managers-only for now (Adam, 16 Sep), like the
  // button it enables; the server refuses the PATCH from anyone else.
  const isManager = !!useAccount().user?.role_permissions?.isManager
  const [copied, setCopied] = useState(false)
  const mintedFor = useRef<string | null>(null)

  // Mint on open, once per artifact. An imperative side effect tied to the
  // dialog opening, which is what the effect's deps say.
  useEffect(() => {
    if (!open || !artifactId) return
    if (mintedFor.current === artifactId) return
    mintedFor.current = artifactId
    setCopied(false)
    void handleShare(artifactId)
  }, [open, artifactId, handleShare])

  const close = () => {
    mintedFor.current = null
    reset()
    onClose()
  }

  const url = state.status === 'shared' || state.status === 'revoking' ? state.share.url : ''
  const discussEnabled = state.status === 'shared' && state.share.discuss_enabled

  const copy = async () => {
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth data-testid="share-artifact-dialog">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LinkIcon fontSize="small" />
        Share {label}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <DialogContentText>
          Anyone with this link can view and download the file, without logging in. Stop sharing and the link goes dead.
        </DialogContentText>
        {state.status === 'error' && (
          <Typography color="error.main" variant="body2">{state.message}</Typography>
        )}
        <TextField
          value={url || (state.status === 'sharing' ? 'Creating link…' : '')}
          fullWidth
          size="small"
          slotProps={{
            htmlInput: { readOnly: true, 'data-testid': 'share-artifact-url', onFocus: (e: React.FocusEvent<HTMLInputElement>) => e.target.select() },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={copied ? 'Copied' : 'Copy link'}>
                    <span>
                      <IconButton size="small" onClick={copy} disabled={!url} aria-label="Copy link">
                        {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            },
          }}
        />
        {/* Opt-in, off by default (Adam, 16 Sep): a discussion spends the
            customer's tokens, and a link can reach a thousand people. Only a
            signed-in colleague ever sees the button; this says they may. */}
        {isManager && <FormControlLabel
          control={
            <Switch
              checked={discussEnabled}
              disabled={state.status !== 'shared'}
              onChange={(e) => { if (artifactId) void handleSetDiscuss(artifactId, e.target.checked) }}
              inputProps={{ 'aria-label': 'Let signed-in colleagues discuss it with Douglas' } as React.InputHTMLAttributes<HTMLInputElement>}
              data-testid="share-artifact-discuss"
            />
          }
          label={
            <span>
              Let signed-in colleagues discuss it with Douglas
              <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                Adds a Discuss button for people logged in to Platinum. Each discussion uses tokens. Never shown to anyone holding only the link.
              </Typography>
            </span>
          }
          sx={{ alignItems: 'flex-start', ml: 0, mt: 0.5 }}
        />}
      </DialogContent>
      <DialogActions>
        <Button
          color="error"
          onClick={async () => { if (artifactId && (await handleRevoke(artifactId))) close() }}
          disabled={!url || state.status === 'revoking'}
          sx={{ mr: 'auto' }}
        >
          Stop sharing
        </Button>
        <Button color="info" onClick={close}>Done</Button>
      </DialogActions>
    </Dialog>
  )
}
